<template>
    <ModalContainer
        :isOpened="!!props.purchase"
        :class="$style.dialog"
        @closeModal="closeModal"
    >
        <template
            v-if="props.purchase"
            #custom-content
        >
            <div :class="$style.content">
                <img
                    v-if="purchaseState === 'ok'"
                    :class="$style.cardOrImage"
                    :src="SuccessPurchaseImage"
                />
                <RewardCard
                    v-else
                    :class="$style.cardOrImage"
                    :type="props.purchase.card.type"
                    :value="props.purchase.card.value"
                    :currency="props.purchase.card.currency"
                />
                <Typography
                    type="header"
                    size="s"
                    :class="$style.title"
                >
                    {{
                        purchaseState === 'ok'
                            ? $tr('rewards.purchase_dialog_success')
                            : $tr('rewards.purchase_dialog_title')
                    }}
                </Typography>
                <TextInput
                    id="reward-purchase"
                    name="reward-purchase"
                    readonly
                    :class="$style.email"
                    :labelText="$tr('rewards.purchase_dialog_email')"
                    :value="props.purchase.email"
                />
                <Typography
                    size="s"
                    :class="$style.description"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        v-html="$tr('rewards.purchase_dialog_description', { country: props.purchase.country.nameEn })"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </Typography>
            </div>
        </template>
        <template
            v-if="props.purchase"
            #actions
        >
            <Button
                v-if="purchaseState === 'none'"
                width="full"
                size="l"
                interfaceTarget="purchase"
                iconLeft="silver_fennec_coin_small"
                @click="doTransaction"
            >
                {{ formatValue(props.purchase.card.price) }}
            </Button>
            <Button
                v-else-if="purchaseState === 'pending'"
                width="full"
                interfaceTarget="loader"
                color="carbon-300"
                size="l"
                icon="loader"
                disabled
            />
            <Button
                v-else-if="purchaseState === 'error'"
                width="full"
                color="rose-400"
                interfaceTarget="do-transaction"
                size="l"
                @click="doTransaction"
            >
                {{ $tr('rewards.purchase_dialog_error') }}
            </Button>
            <Button
                v-else
                width="full"
                interfaceTarget="success-close"
                size="l"
                @click="closeModal"
            >
                {{ $tr('rewards.purchase_dialog_success_button') }}
            </Button>
            <Button
                width="full"
                color="carbon-400"
                size="l"
                interfaceTarget="close"
                :class="$style.non_desktop"
                @click="closeModal"
            >
                {{ $tr('rewards.purchase_dialog_close') }}
            </Button>
        </template>
    </ModalContainer>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import type { RewardCardType, RewardPurchasePayload } from '@/types'
import ModalContainer from '@/components_new/ModalItems/ModalContainer.vue'
import Button from '@/components_new/Button.vue'
import { formatValue } from '@/utils/numbers'
import { RequestStatus } from '@/utils/requests/types'
import Typography from '@/components_new/Typography.vue'
import TextInput from '@/components_new/TextInput/TextInput.vue'
import RewardCard from './RewardCard.vue'
import SuccessPurchaseImage from './images/success_purchase.webp'

const emit = defineEmits<{ closeModal: [] }>()
const props = defineProps<{
    purchase?: {
        card: RewardCardType
        email: string
        country: {
            code: string
            nameEn: string
        }
    }
    doPurchase(payload: RewardPurchasePayload): Promise<void>
}>()

const purchaseState = ref<RequestStatus>('none')

const doTransaction = async () => {
    if (!props.purchase) {
        return
    }
    purchaseState.value = 'pending'
    try {
        await props.doPurchase({
            rewardId: props.purchase.card.id,
            userEmail: props.purchase.email,
            countryCode: props.purchase.country.code,
        })
        purchaseState.value = 'ok'
    } catch {
        purchaseState.value = 'error'
        setTimeout(() => {
            purchaseState.value = 'none'
        }, 1000)
    }
}

const closeModal = () => {
    purchaseState.value = 'none'
    emit('closeModal')
}
</script>
<style module>
.content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
}
.cardOrImage {
    height: 115px;
}
.email {
    width: 100%;
}
.title,
.description {
    text-align: center;
}

.title {
    /* "force" it into two lines without adding <br> into translation */
    width: 80%;
}

.description {
    color: var(--white-60);
}
</style>
